import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type {
  AssociateProfileRestriction,
  AssociateProfileRestrictionsResponse,
} from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';
import type { DataFetchingState } from 'src/types';
import { ignoreCancelError } from 'src/utils/apiClient';

import RestrictionCardLayout from './restrictionCardLayout';

type PageState = DataFetchingState<AssociateProfileRestrictionsResponse>;

const DEFAULT_PAGE_STATE = {
  status: 'loading',
} as const;

const AssociateProfileRestrictions = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);

  const restrictionSort = (a: AssociateProfileRestriction, b: AssociateProfileRestriction) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchRestrictions = async () => {
      setPageState({ status: 'loading' });

      if (!alias || selectedSite.id === 0) {
        setPageState({ status: 'error' });
        return;
      }

      try {
        const response = await ignoreCancelError(
          associateProfileService.getAssociateProfileRestrictions(alias, selectedSite.id, {
            signal,
          }),
          signal,
        );
        setPageState({ status: 'success', data: response.data });
      } catch (error) {
        console.error('Failed to fetch associate profile restrictions', error);
        setPageState({ status: 'error' });
      }
    };

    fetchRestrictions();

    return () => {
      controller.abort();
    };
  }, [alias, selectedSite]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>Oops! Something went wrong.</Text>;
  }

  if (
    pageState.status === 'success' &&
    pageState.data.pending.length === 0 &&
    pageState.data.active.length === 0 &&
    pageState.data.transferredIn.length === 0
  ) {
    return <Text>There were no restrictions found for this Associate</Text>;
  }

  return (
    <Column spacing="400">
      <Column>
        <Text type="b500">
          Pending Restrictions ({pageState.data.pending.length + pageState.data.transferredIn.length})
        </Text>
        {[...pageState.data.pending, ...pageState.data.transferredIn].sort(restrictionSort).map((r) => (
          <RestrictionCardLayout key={`${r.accommodationId}-${r.taskName}`} restriction={r} />
        ))}
      </Column>
      <Column>
        <Text type="b500">Active Restrictions ({pageState.data.active.length})</Text>
        {[...pageState.data.active].sort(restrictionSort).map((r) => (
          <RestrictionCardLayout key={`${r.accommodationId}-${r.taskName}`} restriction={r} />
        ))}
      </Column>
    </Column>
  );
};

export default AssociateProfileRestrictions;
